import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import RichTextEditor from '../components/RichTextEditor';
import { toast } from 'sonner';
import { Loader2, ArrowLeft } from 'lucide-react';

const TicketCreate = () => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    product_id: '',
    category_id: '',
    subcategory_id: '',
    issue_type_id: '',
    priority_id: '',
    assigned_to_id: '',
  });
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [issueTypes, setIssueTypes] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    fetchProducts();
    fetchIssueTypes();
    fetchPriorities();
    // Fetch users and set default assignee if not a client
    if (user && user.role !== 'Client') {
      fetchUsers();
    }
  }, [user]);

  useEffect(() => {
    if (formData.product_id) {
      fetchCategories(formData.product_id);
    }
  }, [formData.product_id]);

  useEffect(() => {
    if (formData.category_id) {
      fetchSubcategories(formData.category_id);
    }
  }, [formData.category_id]);

  const fetchProducts = async () => {
    try {
      const response = await api.get('/products');
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchCategories = async (productId) => {
    try {
      const response = await api.get(`/categories?product_id=${productId}`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchSubcategories = async (categoryId) => {
    try {
      const response = await api.get(`/subcategories?category_id=${categoryId}`);
      setSubcategories(response.data);
    } catch (error) {
      console.error('Error fetching subcategories:', error);
    }
  };

  const fetchIssueTypes = async () => {
    try {
      const response = await api.get('/issue-types');
      setIssueTypes(response.data);
    } catch (error) {
      console.error('Error fetching issue types:', error);
    }
  };

  const fetchPriorities = async () => {
    try {
      const response = await api.get('/priorities');
      setPriorities(response.data);
    } catch (error) {
      console.error('Error fetching priorities:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      // Filter out client users
      const nonClientUsers = response.data.filter(u => u.role !== 'Client');
      setUsers(nonClientUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  // Set default assignee when users are loaded
  useEffect(() => {
    if (user && users.length > 0 && !formData.assigned_to_id) {
      setFormData(prev => ({ ...prev, assigned_to_id: user.id.toString() }));
    }
  }, [user, users]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title || !String(formData.title).trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.description || !String(formData.description).trim()) {
      newErrors.description = 'Description is required';
    }

    if (!formData.product_id) {
      newErrors.product_id = 'Product is required';
    }

    if (!formData.category_id) {
      newErrors.category_id = 'Category is required';
    }

    if (!formData.issue_type_id) {
      newErrors.issue_type_id = 'Issue Type is required';
    }

    if (!formData.priority_id) {
      newErrors.priority_id = 'Priority is required';
    }

    if (user && user.role !== 'Client' && !formData.assigned_to_id) {
      newErrors.assigned_to_id = 'Assignee is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);

    try {
      const payload = {
        ...formData,
        product_id: parseInt(formData.product_id),
        category_id: parseInt(formData.category_id),
        subcategory_id: formData.subcategory_id ? parseInt(formData.subcategory_id) : null,
        issue_type_id: parseInt(formData.issue_type_id),
        priority_id: parseInt(formData.priority_id),
        assigned_to_id: formData.assigned_to_id ? parseInt(formData.assigned_to_id) : null,
      };

      const response = await api.post('/tickets', payload);
      toast.success('Ticket created successfully!');
      navigate(`/tickets/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create ticket');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4 md:space-y-6" data-testid="ticket-create-page">
      <div className="flex items-start sm:items-center gap-2 sm:gap-3 md:gap-4">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          data-testid="back-btn"
          className="gap-2 hover:bg-slate-100 h-8 sm:h-9 px-2 sm:px-3 text-xs sm:text-sm"
        >
          <ArrowLeft size={16} className="sm:w-5 sm:h-5" />
          <span className="hidden sm:inline">Back</span>
        </Button>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Create New Ticket</h1>
          <p className="text-xs sm:text-sm text-slate-600 mt-0.5">Fill in the details to create a new ticket</p>
        </div>
      </div>

      <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-900/80 shadow-sm bg-white">
        <CardHeader className="pb-3 sm:pb-4 md:pb-6">
          <CardTitle className="text-base sm:text-lg md:text-xl">Ticket Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 sm:space-y-6">
          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6" data-testid="ticket-form">
            <div className="space-y-1.5 sm:space-y-2">
              <Label htmlFor="title" className="text-xs sm:text-sm font-semibold text-slate-700">Title *</Label>
              <Input
                id="title"
                data-testid="ticket-title-input"
                placeholder="Brief description of the issue"
                value={formData.title}
                onChange={(e) => {
                  setFormData({ ...formData, title: e.target.value });
                  if (errors.title) setErrors({ ...errors, title: '' });
                }}
                required
                className={`border-slate-300 focus:border-indigo-500 focus:ring-indigo-500 text-xs sm:text-sm ${
                  errors.title ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                }`}
              />
              {errors.title && <p className="text-xs sm:text-sm text-red-500">{errors.title}</p>}
            </div>

            <div className="space-y-1.5 sm:space-y-2">
              <Label htmlFor="description" className="text-xs sm:text-sm font-semibold text-slate-700">Description *</Label>
              <RichTextEditor
                value={formData.description}
                onChange={(value) => {
                  setFormData({ ...formData, description: value });
                  if (errors.description) setErrors({ ...errors, description: '' });
                }}
                placeholder="Detailed description of the issue..."
              />
              {errors.description && <p className="text-xs sm:text-sm text-red-500">{errors.description}</p>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 md:gap-6">
              <div className="space-y-1.5 sm:space-y-2">
                <Label htmlFor="product" className="text-xs sm:text-sm font-semibold text-slate-700">Product *</Label>
                <Select
                  value={formData.product_id}
                  onValueChange={(value) => {
                    setFormData({ ...formData, product_id: value, category_id: '', subcategory_id: '' });
                    setCategories([]);
                    setSubcategories([]);
                    if (errors.product_id) setErrors({ ...errors, product_id: '' });
                  }}
                  required
                >
                  <SelectTrigger 
                    data-testid="product-select"
                    className={`border-slate-300 focus:border-indigo-500 focus:ring-indigo-500 ${
                      errors.product_id ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                    }`}
                  >
                    <SelectValue placeholder="Select product" />
                  </SelectTrigger>
                  <SelectContent searchable>
                    {products.map((p) => (
                      <SelectItem key={p.id} value={p.id.toString()}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.product_id && <p className="text-sm text-red-500">{errors.product_id}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="category" className="text-sm font-semibold text-slate-700">Category *</Label>
                <Select
                  value={formData.category_id}
                  onValueChange={(value) => {
                    setFormData({ ...formData, category_id: value, subcategory_id: '' });
                    setSubcategories([]);
                    if (errors.category_id) setErrors({ ...errors, category_id: '' });
                  }}
                  disabled={!formData.product_id}
                  required
                >
                  <SelectTrigger 
                    data-testid="category-select"
                    className={`border-slate-300 focus:border-indigo-500 focus:ring-indigo-500 ${
                      errors.category_id ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                    }`}
                  >
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => (
                      <SelectItem key={c.id} value={c.id.toString()}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.category_id && <p className="text-sm text-red-500">{errors.category_id}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="subcategory" className="text-sm font-semibold text-slate-700">Subcategory</Label>
                <Select
                  value={formData.subcategory_id}
                  onValueChange={(value) => setFormData({ ...formData, subcategory_id: value })}
                  disabled={!formData.category_id || subcategories.length === 0}
                >
                  <SelectTrigger data-testid="subcategory-select">
                    <SelectValue placeholder="Select subcategory (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {subcategories.map((s) => (
                      <SelectItem key={s.id} value={s.id.toString()}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="issue_type" className="text-sm font-semibold text-slate-700">Issue Type *</Label>
                <Select
                  value={formData.issue_type_id}
                  onValueChange={(value) => {
                    setFormData({ ...formData, issue_type_id: value });
                    if (errors.issue_type_id) setErrors({ ...errors, issue_type_id: '' });
                  }}
                  required
                >
                  <SelectTrigger 
                    data-testid="issue-type-select"
                    className={`border-slate-300 focus:border-indigo-500 focus:ring-indigo-500 ${
                      errors.issue_type_id ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                    }`}
                  >
                    <SelectValue placeholder="Select issue type" />
                  </SelectTrigger>
                  <SelectContent>
                    {issueTypes.map((i) => (
                      <SelectItem key={i.id} value={i.id.toString()}>{i.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.issue_type_id && <p className="text-sm text-red-500">{errors.issue_type_id}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="priority" className="text-sm font-semibold text-slate-700">Priority *</Label>
                <Select
                  value={formData.priority_id}
                  onValueChange={(value) => {
                    setFormData({ ...formData, priority_id: value });
                    if (errors.priority_id) setErrors({ ...errors, priority_id: '' });
                  }}
                  required
                >
                  <SelectTrigger 
                    data-testid="priority-select"
                    className={`border-slate-300 focus:border-indigo-500 focus:ring-indigo-500 ${
                      errors.priority_id ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                    }`}
                  >
                    <SelectValue placeholder="Select priority" />
                  </SelectTrigger>
                  <SelectContent>
                    {priorities.map((p) => (
                      <SelectItem key={p.id} value={p.id.toString()}>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: p.color }} />
                          {p.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.priority_id && <p className="text-sm text-red-500">{errors.priority_id}</p>}
              </div>

              {user && user.role !== 'Client' && (
                <div className="space-y-2">
                  <Label htmlFor="assigned_to" className="text-sm font-semibold text-slate-700">Assign To *</Label>
                  <Select
                    value={formData.assigned_to_id}
                    onValueChange={(value) => {
                      setFormData({ ...formData, assigned_to_id: value });
                      if (errors.assigned_to_id) setErrors({ ...errors, assigned_to_id: '' });
                    }}
                    required
                  >
                    <SelectTrigger 
                      data-testid="assigned-to-select"
                      className={`border-slate-300 focus:border-indigo-500 focus:ring-indigo-500 ${
                        errors.assigned_to_id ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                      }`}
                    >
                      <SelectValue placeholder="Select assignee" />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map((u) => (
                        <SelectItem key={u.id} value={u.id.toString()}>
                          <div className="flex flex-col">
                            <span>
                              {u.full_name || u.username}
                              {user && u.id === user.id && ' (me)'}
                            </span>
                            <span className="text-xs text-slate-500">{u.role}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.assigned_to_id && <p className="text-sm text-red-500">{errors.assigned_to_id}</p>}
                </div>
              )}
            </div>

            <div className="flex gap-4 pt-4">
              <Button
                type="submit"
                data-testid="submit-ticket-btn"
                className="bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-700 hover:to-indigo-600 shadow-lg shadow-indigo-500/30 text-white"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Ticket'
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
                disabled={loading}
                className="border-slate-300 hover:bg-slate-100"
              >
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default TicketCreate;
